import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import LocationItem from "./LocationItem";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { setSelectedGuest, setSelectedLocation } from "../store/checkoutSlice";
import AutocompleteDropdown, {
  DropdownOption,
} from "../../../ui/molecules/AutocompleteDropdown";
import { setError } from "../store/uiSlice";
import useDebounce from "../../../hooks/useDebounce";
import GuestItem from "./GuestItem";
import RecentTransactions from "./RecentTransactions";
import {
  useGetGuestsQuery,
  useGetLocationTransactionsQuery,
  useGetLocationsQuery,
} from "../services/managersApi";
import { skipToken } from "@reduxjs/toolkit/query";
import BackButton from "./BackButton";

export const StyledDinerSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const DinerSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const location = useSelector(
    (state: RootState) => state.checkout.selectedLocation,
  );
  const selectedGuest = useSelector(
    (state: RootState) => state.checkout.selectedGuest,
  );
  const [guestQuery, setGuestQuery] = useState("");
  const debouncedGuestQuery = useDebounce(guestQuery, 225);

  const { data: guestsData, error: guestsError } = useGetGuestsQuery(
    debouncedGuestQuery.length >= 3 ? debouncedGuestQuery : skipToken,
  );

  // Get location_id from query params, fall back to location?.id if not present
  const queryLocationId = searchParams.get("location_id") ?? null;
  const locationId = queryLocationId || location?.id;

  // Conditionally fetch location data when queryLocationId is provided
  const { data: locationData } = useGetLocationsQuery(
    queryLocationId
      ? { type: "location_id", value: queryLocationId }
      : skipToken,
  );

  const {
    data: transactionsData,
    error: transactionsError,
    isFetching,
  } = useGetLocationTransactionsQuery(locationId ?? skipToken, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (transactionsError) {
      dispatch(setError("Failed to fetch transactions."));
    } else {
      dispatch(setError(""));
    }
  }, [transactionsError, dispatch]);

  useEffect(() => {
    if (
      queryLocationId &&
      locationData?.locations &&
      locationData.locations.length > 0
    ) {
      // Set the location from the query results
      dispatch(setSelectedLocation(locationData.locations[0]));
    }
  }, [queryLocationId, locationData, dispatch]);

  useEffect(() => {
    if (guestsError) {
      dispatch(setError("Failed to fetch locations. Please try again."));
    } else {
      dispatch(setError(""));
    }
  }, [guestsError, dispatch]);

  const guests = guestsData?.users ?? [];
  const transactions = transactionsData?.transactions ?? [];

  const handleGuestSelect = (guest: DropdownOption) => {
    dispatch(setSelectedGuest(guests.find((g) => g.id === guest?.id) ?? null));
    navigate("/diner-profile");
  };

  const handleGuestSearch = (query: string) => {
    setGuestQuery(query);
  };

  return (
    <StyledDinerSelection>
      <BackButton
        onClick={() => {
          navigate("/location-selection");
          dispatch(setSelectedLocation(null));
          dispatch(setSelectedGuest(null));
        }}
      />
      {location && <LocationItem location={location} isDiner={true} />}
      {!selectedGuest && (
        <AutocompleteDropdown
          options={guests.map((guest) => ({
            label: <GuestItem guest={guest} />,
            value: guest.id.toString(),
            id: guest.id,
          }))}
          onChange={handleGuestSelect}
          selectedOption={null}
          placeholder="Search for a guest"
          onSearchChange={handleGuestSearch}
          isServerSide={true}
        />
      )}
      <RecentTransactions
        transactions={transactions}
        isUserTransaction={false}
        isLoading={isFetching}
      />
    </StyledDinerSelection>
  );
};

export default DinerSelection;
